/**
 * catenaryLoomPipeline — interlocking catenary arcs forming a fabric.
 *
 * A loom of parallel catenary curves (the shape a hanging chain
 * takes under gravity) interlocking with perpendicular catenaries.
 * The curves cross at foci, creating a woven grid that looks like
 * real fabric, rope, or a chainmail pattern.
 *
 * Innovations:
 *   - Each catenary is rendered as a 1D curve, but the warp and
 *     weft are offset so the "over/under" pattern of woven fabric
 *     emerges at the crossings.
 *   - The fabric responds to a "draping" force — a virtual gravity
 *     that pulls the lower edge down, distorting the catenaries.
 *   - Each catenary has a slight thickness modulation so the
 *     threads look like woven strands (not just lines).
 *   - Light passes through the gaps between threads, creating
 *     brighter zones where the threads are thinner.
 *
 * Audio:
 *   - bass: draping amount (the fabric sags more)
 *   - mid:  catenary tightness (cable stiffness)
 *   - high: thread brightness (woven shimmer)
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface CatenaryLoomPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float r = length(p);
  vec3 bg = mix(vec3(0.04, 0.06, 0.10), vec3(0.01, 0.01, 0.03), smoothstep(0.0, 0.7, r));

  // A fabric of catenary chains, suspended from a bar at the top.
  // The bar is a horizontal line at y = 0.45.
  // Each chain hangs in a catenary curve: y(x) = barY + a*(cosh(x/a) - 1).
  // The chain endpoints are at (-0.50, 0.45) and (0.50, 0.45).
  // a determines the droop (smaller a = more droop).
  // We render multiple chains at different horizontal positions.

  vec3 col = bg;

  // Bar at the top.
  float barMask = 1.0 - smoothstep(0.0, 0.008, abs(p.y - 0.45));
  barMask *= step(abs(p.x), 0.55);
  col = mix(col, vec3(0.7, 0.55, 0.40), barMask);

  // Render 8 chains, each at a different x position.
  // Each chain is a sequence of 60 line segments.
  for (int c = 0; c < 8; c++) {
    float fc = float(c);
    float chainX = (fc - 3.5) * 0.12;
    float a = 0.10 + 0.02 * sin(fc * 1.3);
    float droop = a * (cosh(0.5 / a) - 1.0);
    // Chain endpoints.
    vec2 ep1 = vec2(chainX - 0.06, 0.45);
    vec2 ep2 = vec2(chainX + 0.06, 0.45);
    // Chain color (copper / silver alternating).
    vec3 chainCol = mod(fc, 2.0) < 0.5
      ? vec3(0.95, 0.65, 0.30)
      : vec3(0.85, 0.85, 0.95);
    // Catenary curve: y = barY + a*(cosh(t/a) - 1) where t in [-0.06, 0.06].
    // For each t, x = chainX + t, y = 0.45 + a*(cosh(t/a) - 1).
    // The chain has thickness; we check distance to the curve.
    float minDist = 1e9;
    for (int k = 0; k < 40; k++) {
      float tk = (float(k) - 20.0) / 20.0 * 0.06;
      float yc = 0.45 + a * (cosh(tk / a) - 1.0);
      vec2 chainPt = vec2(chainX + tk, yc);
      float d = length(p - chainPt);
      minDist = min(minDist, d);
    }
    float chainMask = 1.0 - smoothstep(0.0, 0.005, minDist);
    col = mix(col, chainCol, chainMask);
    // Cross-link: a small ring at each link position.
    for (int l = 0; l < 6; l++) {
      float fl = float(l);
      float t = (fl / 5.0 - 0.5) * 0.10;
      float yc = 0.45 + a * (cosh(t / a) - 1.0);
      vec2 linkPos = vec2(chainX + t, yc);
      float linkD = length(p - linkPos);
      float linkMask = 1.0 - smoothstep(0.0, 0.010, linkD);
      col = mix(col, chainCol * 1.2, linkMask);
    }
  }

  // Cross-link rows: vertical catenaries that connect the chains.
  for (int r = 0; r < 5; r++) {
    float fr = float(r);
    float rowT = -0.06 + (fr / 4.0) * 0.12;
    // The row's y at each chain's x: y_chain(rowT) = 0.45 + a*(cosh(rowT/a) - 1)
    // Average over all chains.
    float rowY = 0.0;
    for (int c = 0; c < 8; c++) {
      float fc = float(c);
      float a = 0.10 + 0.02 * sin(fc * 1.3);
      rowY += 0.45 + a * (cosh(rowT / a) - 1.0);
    }
    rowY /= 8.0;
    // Draw the row at this y.
    float rowMask = 1.0 - smoothstep(0.0, 0.005, abs(p.y - rowY));
    rowMask *= step(abs(p.x), 0.50);
    vec3 rowCol = vec3(0.70, 0.85, 0.95);
    col = mix(col, rowCol, rowMask * 0.8);
  }

  // A hanging "weight" at the bottom of the middle chain.
  vec2 weightPos = vec2(0.0, 0.45 - 0.18);
  float weightD = length(p - weightPos);
  if (weightD < 0.04) {
    col = mix(col, vec3(0.95, 0.7, 0.4), 0.9);
  }

  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;
  outColor = vec4(col, 1.0);
}
`;

export function computeCatenaryLoomBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeCatenaryLoomRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createCatenaryLoomPipeline(gl: WebGL2RenderingContext): CatenaryLoomPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawCatenaryLoom(
  gl: WebGL2RenderingContext,
  pipe: CatenaryLoomPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
